import { TechIcon } from '@codeware/shared/ui/primitives';
import type { PillListBlock as PillListBlockProps } from '@codeware/shared/util/payload-types';
import { cn } from '@codeware/shared/util/ui';

import { interactiveSurface } from '../../utils/interactive-surface';
import { OwnLogo } from '../../utils/OwnLogo';

type Pill = NonNullable<PillListBlockProps['items']>[number];

/** The mark from the platform's list, else the tenant's own, else nothing */
function PillLogo({ pill }: { pill: Pill }) {
  if (pill.icon) {
    return <TechIcon brand={pill.icon} className="size-4" />;
  }

  return <OwnLogo logo={pill.logo} size={16} />;
}

/**
 * Pill list block — labelled pills with optional links and logos.
 *
 * Draws no surface of its own: a band behind it is the block's `band` setting,
 * which the renderer draws for every block alike.
 */
export const PillListBlock: React.FC<PillListBlockProps> = ({
  eyebrow,
  heading,
  intro,
  items
}) => {
  if (!items?.length) return null;

  return (
    <section>
      <div className="mb-9 max-w-xl">
        {eyebrow && (
          <p className="text-core-link text-sm font-semibold tracking-[0.14em] uppercase">
            {eyebrow}
          </p>
        )}
        {heading && (
          <h2 className="text-core-headline mt-3 text-3xl font-semibold tracking-tight">
            {heading}
          </h2>
        )}
        {intro && (
          <p className="text-muted-foreground mt-3.5 text-base leading-relaxed">
            {intro}
          </p>
        )}
      </div>

      {/* Each pill carries its own surface: in some themes the border is the
          very colour of a subtle band, and a pill made of border alone
          disappears into it */}
      <div className="flex flex-wrap gap-2">
        {items.map((item, i) =>
          item.url ? (
            <a
              key={i}
              href={item.url}
              target="_blank"
              rel="noreferrer"
              className={cn(
                'text-foreground inline-flex items-center gap-2 rounded-full px-3 py-1 font-mono text-sm shadow-xs',
                interactiveSurface({ shape: 'pill' })
              )}
            >
              <PillLogo pill={item} />
              {item.label}
            </a>
          ) : (
            <span
              key={i}
              className="border-border bg-card text-foreground inline-flex items-center gap-2 rounded-full border px-3 py-1 font-mono text-sm shadow-xs"
            >
              <PillLogo pill={item} />
              {item.label}
            </span>
          )
        )}
      </div>
    </section>
  );
};
